import React, { useEffect, useMemo, useRef } from 'react';
import { useUser } from './UserContext';

const CONSENT_CONFIG = {
	apiBaseUrl: 'https://api.dev-v5.privasapien.com',
	tenant: '610b7a9c-72ea-11f0-998f-3e3dd1ad87e5',
	appKey: '608f19271eee2cfcdd9feba809771d9c6752a20c1bd3a0fdce967e89f1fe2ba1',
	applicationId: '01a0a4f8-f520-7d54-9657-71c06bdc77f9',
	modeOfCommunication: 'email',
	defaultLanguage: 'en',
	lang: 'en',
	continueUrl: 'https://www.google.com',
	age: 25,
	parentEmail: '',
};

// Fires the event the consent-banner web component listens for on window.
// A bare open-consent-banner event asks "do you need anything from this person?" and
// stays closed when consent is already on record. `force: true` is the event-side
// equivalent of calling .open(): it always shows a surface, landing on the SDK's
// already-consented screen (Update my consent / Continue) when there is nothing to ask.
// That is what "Manage Consent" in the header means, so it is required here, not optional.
export const openConsentBanner = () => {
	window.dispatchEvent(new CustomEvent('open-consent-banner', { detail: { force: true } }));
};

const ConsentBanner = () => {
	const { user } = useUser();
	const userIdentifier = user?.email || '';
	const bannerRef = useRef(null);

	const config = useMemo(
		() =>
			userIdentifier
				? {
						...CONSENT_CONFIG,
						userIdentifier,
						unique_id_details: {
							value: userIdentifier,
							type: 'string',
						},
					}
				: null,
		[userIdentifier],
	);

	// config is handed to the element as a property, not as a JSON string prop, and only once
	// the element is upgraded. React 19 decides between property and attribute for custom
	// elements with `key in domElement`, so a `config={JSON.stringify(...)}` prop lands as an
	// attribute (parsed by the SDK) or as a raw string on the property (not parsed) depending
	// on whether the async SDK script has upgraded the element yet -- a per-load race that
	// surfaces as "config is missing required field(s)". Setting it here sidesteps that.
	// autoOpen must be set first: the SDK reads it via hasAttribute() inside renderApp(), which
	// only runs when the config setter fires.
	useEffect(() => {
		if (!config) {
			return undefined;
		}

		let cancelled = false;

		customElements.whenDefined('consent-banner').then(() => {
			const el = bannerRef.current;
			if (cancelled || !el) {
				return;
			}
			el.setAttribute('autoOpen', '');
			el.config = config;
		});

		return () => {
			cancelled = true;
		};
	}, [config]);

	// Nothing is rendered -- and so the SDK is never invoked -- until the user is known.
	if (!config) {
		return null;
	}

	return <consent-banner ref={bannerRef} id="consentBanner" />;
};

export default ConsentBanner;
